/**
 * Mioty V2 — moduł zarządzania miotami (UI 2.0).
 * AKTUALNE. Zastępuje legacy ProfilePanel?tab=offer.
 *
 * Dane: GET /api/litters. Dodaj/Edytuj → LitterWizard. Usuń → DELETE /api/litters/{id}.
 * Podgląd → publiczna strona /miot/{slug} (jeśli opublikowany).
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Dog } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { ModuleListV2, ModuleCardV2, ConfirmDeleteDialog, useModuleDelete } from './ModuleListV2';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  planned: { label: 'Planowany', className: 'bg-zinc-100 text-zinc-600' },
  expecting: { label: 'Oczekiwany', className: 'bg-amber-50 text-amber-700' },
  born: { label: 'Urodzony', className: 'bg-emerald-50 text-emerald-700' },
  available: { label: 'Dostępne szczenięta', className: 'bg-emerald-50 text-emerald-700' },
  reserved: { label: 'Zarezerwowane', className: 'bg-blue-50 text-blue-600' },
  sold: { label: 'Sprzedane', className: 'bg-zinc-100 text-zinc-500' },
};

export default function MiotyV2() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const redirectedRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/litters`, { headers });
      setItems(res.data || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Brak miotów → od razu kreator (pomijamy pusty ekran „Dodaj miot").
  useEffect(() => {
    if (loading || redirectedRef.current) return;
    if (items.length === 0) {
      redirectedRef.current = true;
      navigate('/wizard/miot', { replace: true });
    }
  }, [loading, items, navigate]);

  const del = useModuleDelete(
    async (litter) => {
      await axios.delete(`${API}/litters/${litter.id}`, { headers });
      toast.success('Miot usunięty');
    },
    load
  );

  return (
    <>
      <ModuleListV2
        activeKey="litters"
        contextLabel="Mioty"
        icon={Dog}
        title="Mioty"
        subtitle="Aktualne i planowane mioty szczeniąt"
        addLabel="Dodaj miot"
        onAdd={() => navigate('/wizard/miot')}
        loading={loading || items.length === 0}
        items={items}
        empty={{
          icon: Dog,
          title: 'Nie masz jeszcze dodanego miotu',
          text: 'Dodaj prezentację miotu lub zapowiedź szczeniąt. Możesz powiązać ją ze stroną hodowli teraz albo później.',
        }}
      >
        {items.map((l) => {
          const parents = [l.mother_name || l.dam_name, l.father_name || l.sire_name].filter(Boolean).join(' × ');
          const st = STATUS_LABEL[l.status];
          return (
            <ModuleCardV2
              key={l.id}
              id={l.id}
              image={l.cover_image || l.mother_photo || l.father_photo}
              title={l.name || l.breed || 'Miot'}
              subtitle={parents || l.breed}
              badges={[
                ...(st ? [st] : []),
                ...(l.available_count ? [{ label: `${l.available_count} dostępne`, className: 'bg-orange-50 text-orange-600' }] : []),
              ]}
              onEdit={() => navigate(`/wizard/miot/${l.id}`)}
              onPreview={l.slug ? () => window.open(`/miot/${l.slug}`, '_blank') : undefined}
              onDelete={() => del.setTarget(l)}
            />
          );
        })}
      </ModuleListV2>
      <ConfirmDeleteDialog {...del.dialogProps} />
    </>
  );
}
